import type { AbilityBuilder } from "@casl/ability";

import type { AbilityContext, AppAbility } from "./ability";

export type DefineRule = AbilityBuilder<AppAbility>["can"];

/** The record conditions a caller's context grants, shared by every role's rules. */
export const ruleConditionsFor = (context: AbilityContext) => ({
  ownCase: { userId: context.userId ?? "" },
  ownDocument: { uploadedBy: context.userId ?? "" },
  assignedCase: { id: { $in: [...(context.assignedCaseIds ?? [])] } },
  editableCaseDocument: { caseId: { $in: [...(context.editableCaseIds ?? [])] } },
  assignedCaseTask: {
    contextType: "case" as const,
    contextId: { $in: [...(context.assignedCaseIds ?? [])] },
  },
  claimScopes: [
    { clientId: { $in: [...(context.clientIds ?? [])] } },
    ...((context.branchIds?.length ?? 0) > 0
      ? [{ branchId: { $in: [...(context.branchIds ?? [])] } }]
      : []),
  ],
  openCreationReview: {
    id: context.hasOpenCaseReviewTask ? (context.caseId ?? "") : "",
  },
  assignedAnalysisReview: {
    id: context.hasAnalysisReviewTask ? (context.caseId ?? "") : "",
  },
  assignedTask: { assignedTo: context.username ?? "" },
  createdTask: { "createdBy.id": context.username ?? "" },
});

export type RuleConditions = ReturnType<typeof ruleConditionsFor>;

/** Rules every case-reviewing role shares while it holds the case's analysis review. */
export const defineAnalysisReviewRules = (can: DefineRule, conditions: RuleConditions) => {
  can("update", "Case", ["vehicle", "debt"], {
    ...conditions.assignedAnalysisReview,
    caseType: "debt_collection",
  });
  can(["reviewAnalysis", "markSectionReviewed"], "Case", conditions.assignedAnalysisReview);
  can("accessAnalysisReview", "Case", conditions.ownCase);
  can("accessAnalysisReview", "Case", conditions.assignedAnalysisReview);
};

export const PARTNER_ACTIVE_CASE_STEPS = [
  "negotiation",
  "legal_analysis",
  "legal_process",
  "in_payment",
] as const;

export const CASE_GENERAL_FIELDS = [
  "clientId",
  "branchId",
  "externalCode",
  "incident",
  "liableVehicles",
  "vehicle",
  "liabilityAnalysis",
] as const;

/** Fields the assigned liability-analysis reviewer may correct even on a case they don't own. */
export const CASE_REVIEW_CORRECTION_FIELDS = [
  "incident",
  "liableVehicles",
  "contacts",
  "damages",
] as const;

export const CLIENT_FIELDS_EXCEPT_NOTES = [
  "type",
  "name",
  "dateOfBirth",
  "profession",
  "referral",
  "contact",
  "tradeName",
  "companyType",
  "economicGroup",
  "address",
  "mainContact",
  "additionalContacts",
  "leadSource",
  "salesConsultantId",
  "fleet",
  "contract",
  "paymentTerms",
  "taxId",
  "status",
  "primaryDocumentContactUsername",
  "branches",
] as const;
